/**
 * Custom hook that provides a standardized template replacer function
 * with support for standard client/organization tokens and custom overrides
 */

import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import { makeSelectClientLanding } from 'containers/ClientLanding/selectors';
import { replaceTemplatePlaceholders } from 'utils/templateUtils';

/**
 * Custom hook that returns a template replacer function with standard token support
 * @returns {Object} Object containing the replacer function and standard tokens
 */
const useTemplateReplacer = () => {
  // Get client details and landing from Redux store
  const clientDetails = useSelector(makeSelectClientDetails());
  const clientLanding = useSelector(makeSelectClientLanding());

  /**
   * Standard tokens that are always available
   * These can be overridden by providing custom values
   */

  const clientName = clientDetails?.name;
  const clientShortName = clientDetails?.shortName;
  const clientFormalShortName = clientDetails?.formalShortName;

  const userTerm = clientLanding?.userTerm;

  const standardTokens = useMemo(
    () => ({
      // Client tokens
      clientName,
      clientTitle: clientName,
      clientSuffix: clientName,
      clientFormalShortName,
      clientShortName,
      // User/Landing tokens
      userTerm: userTerm || '',
    }),
    [clientName, clientShortName, clientFormalShortName, userTerm],
  );

  /**
   * Main token replacement function that combines standard tokens with custom overrides
   *
   * @param {string} template - The template string containing placeholders
   * @param {Object} customTokens - Custom token values that override standard ones
   * @param {Object} options - Options for the replacement (caseSensitive, formats, etc.)
   * @returns {string} - The template with placeholders replaced
   */
  const replaceTokens = useCallback(
    (template, customTokens = {}, options = {}) => {
      // Merge standard tokens with custom overrides
      // Custom tokens take precedence over standard ones
      const allTokens = {
        ...standardTokens,
        ...customTokens,
      };

      // Default options that work well for most cases
      const defaultOptions = {
        caseSensitive: false, // More flexible for template authors
        formats: ['#', '<>', '{}'], // Support all common formats
      };

      const finalOptions = {
        ...defaultOptions,
        ...options,
      };

      return replaceTemplatePlaceholders(template, allTokens, finalOptions);
    },
    [standardTokens],
  );

  return {
    replaceTokens,
    standardTokens,
  };
};

export default useTemplateReplacer;
